import { useEffect, useState } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { isStandaloneDisplayMode } from '../utils/displayMode';
import { useInstallPrompt } from '../hooks/useInstallPrompt';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { dismissA2hsCoach } from '../api/users';

const LOCAL_DISMISS_KEY = 'valentine-a2hs-coach-dismissed';
const TRIGGER_DELAY_MS = 4000;
/** MySettings' "Install Valentine on this phone" re-entry (Task 7 Step 8)
 * dispatches this on `window` to re-open the ALREADY-MOUNTED coach singleton
 * (App.tsx) without a page reload — see `triggerA2hsCoach` below. */
const FORCE_SHOW_EVENT = 'valentine:a2hs-coach-show';

type Platform = 'ios' | 'android' | 'other';

function detectPlatform(): Platform {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  if (/android/i.test(ua)) return 'android';
  return 'other';
}

/** Exported for MySettings' re-entry button (P3-A5/A6): re-opens the coach
 * ignoring BOTH the local-fast-path AND server-side dismissal flags — a
 * staffer setting up a new phone, or who dismissed the coach before and
 * changed their mind, gets the install steps back on demand. Still respects
 * the mobile/standalone gate (there's nothing to show on desktop, or inside
 * an already-installed PWA). */
// eslint-disable-next-line react-refresh/only-export-components
export function triggerA2hsCoach(): void {
  window.dispatchEvent(new Event(FORCE_SHOW_EVENT));
}

export interface InstallCoachProps {
  /** P3-A12/Task 8: called after the user grants Notification permission in
   * the coach's final step. Task 7 only requests permission (P3-A2) — the
   * actual `PushManager.subscribe()` + backend POST is wired at the mount
   * site (`App.tsx`), which passes `() => void subscribe()` from
   * `usePushSubscription()`. `Notification.requestPermission()` has already
   * resolved 'granted' by the time this fires, so `subscribe()`'s own
   * internal `requestPermission()` call just re-resolves immediately with
   * no second prompt. */
  onPermissionGranted?: () => void;
}

export default function InstallCoach({ onPermissionGranted }: InstallCoachProps = {}) {
  const isMobile = useIsMobile();
  const { data: user } = useCurrentUser();
  const { canInstall, promptInstall } = useInstallPrompt();
  const [visible, setVisible] = useState(false);
  const [permissionStep, setPermissionStep] = useState(false);

  const locallyDismissed = localStorage.getItem(LOCAL_DISMISS_KEY) === 'true';
  const serverDismissed = !!user?.a2hs_coach_dismissed_at;
  const alreadyDismissed = locallyDismissed || serverDismissed;
  const standalone = isStandaloneDisplayMode();

  // P3-A6: "first meaningful interaction (e.g. first task completion) or 4s,
  // whichever first." Treated app-wide (first pointer/keyboard interaction
  // anywhere in the authenticated app) rather than instrumenting Task
  // Center specifically — see this task's Scope Decision note in the plan.
  //
  // Listens on `click`/`keyup`, NOT `pointerdown`/`keydown` (final-review
  // fix, found by CI's mobile-timeclock-nav smoke spec): pointerdown fires
  // BEFORE the browser resolves click/navigation for that same gesture, so
  // if a user's very first tap in the app happens to BE a bottom-tab-bar
  // nav button, the coach's fixed bottom sheet (zIndex 1000, up to 70dvh)
  // could mount between pointerdown and pointerup and steal the tap that
  // was supposed to navigate. click/keyup fire only after the originating
  // element's own handler has already run, so by the time this listener's
  // show() call schedules the coach, the tap it reacted to has already done
  // its job.
  useEffect(() => {
    if (!isMobile || standalone || alreadyDismissed || !user) return;
    let shown = false;
    const show = () => {
      if (shown) return;
      shown = true;
      setVisible(true);
    };
    const timer = setTimeout(show, TRIGGER_DELAY_MS);
    const onFirstInteraction = () => show();
    document.addEventListener('click', onFirstInteraction, { once: true });
    document.addEventListener('keyup', onFirstInteraction, { once: true });
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', onFirstInteraction);
      document.removeEventListener('keyup', onFirstInteraction);
    };
  }, [isMobile, standalone, alreadyDismissed, user]);

  // MySettings re-entry (P3-A5/A6): ignores dismissal entirely, but still
  // respects mobile/standalone — installing makes no sense on desktop or
  // from inside the already-installed app.
  useEffect(() => {
    if (!isMobile || standalone) return;
    const handler = () => {
      setPermissionStep(false);
      setVisible(true);
    };
    window.addEventListener(FORCE_SHOW_EVENT, handler);
    return () => window.removeEventListener(FORCE_SHOW_EVENT, handler);
  }, [isMobile, standalone]);

  if (!visible) return null;

  const dismiss = () => {
    localStorage.setItem(LOCAL_DISMISS_KEY, 'true');
    setVisible(false);
    setPermissionStep(false);
    void dismissA2hsCoach().catch(() => {
      // Best-effort — the localStorage fast-path already prevents re-nagging
      // on THIS device even if the server call fails; a retry happens
      // naturally next time MySettings or the app loads user data fresh.
    });
  };

  const handleAllow = () => {
    if (typeof Notification === 'undefined' || !Notification.requestPermission) {
      dismiss();
      return;
    }
    // P3-A2, Gemini-verified pattern: `Notification.requestPermission()` is
    // called as the LITERAL FIRST STATEMENT of this handler — before any
    // `await` — so the permission prompt still rides the original tap's
    // user-gesture stack. (An async function's body runs synchronously up
    // to its first `await`; this handler has none before the call, so a
    // `.then()` chain here is equivalent to and safer than making the
    // handler itself `async` and awaiting up front.)
    Notification.requestPermission().then((permission) => {
      if (permission === 'granted') {
        onPermissionGranted?.();
      }
      dismiss();
    });
  };

  const platform = detectPlatform();

  return (
    <div
      role="dialog"
      aria-label="Install Valentine"
      style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 1000,
        background: 'var(--card, #fff)', borderTopLeftRadius: 16, borderTopRightRadius: 16,
        boxShadow: '0 -4px 24px rgba(0,0,0,0.15)',
        padding: '1.25rem 1.25rem calc(1.25rem + env(safe-area-inset-bottom, 0px))',
        maxHeight: '70dvh', overflowY: 'auto',
      }}
    >
      {!permissionStep ? (
        <>
          <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.05rem', fontWeight: 700 }}>
            Put your tasks one tap away
          </h2>
          <p style={{ margin: '0 0 1rem', fontSize: '0.88rem', color: 'var(--text-muted)' }}>
            Install Valentine on your phone's home screen — no app store, no download.
          </p>
          {platform === 'ios' && (
            <ol style={{ fontSize: '0.85rem', paddingLeft: '1.2rem', marginBottom: '1rem' }}>
              <li>Tap the Share icon in Safari's toolbar</li>
              <li>Scroll down and tap "Add to Home Screen"</li>
              <li>Tap "Add" in the top right</li>
            </ol>
          )}
          {platform === 'android' && canInstall && (
            <p style={{ fontSize: '0.85rem', marginBottom: '1rem' }}>
              Tap "Install" below and confirm the prompt.
            </p>
          )}
          {(platform === 'other' || (platform === 'android' && !canInstall)) && (
            <p style={{ fontSize: '0.85rem', marginBottom: '1rem' }}>
              Open your browser's menu and look for "Add to Home Screen" or "Install app".
            </p>
          )}
          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
            {platform === 'android' && canInstall && (
              <button
                onClick={async () => {
                  await promptInstall();
                  setPermissionStep(true);
                }}
                style={{ padding: '0.6rem 1.1rem', borderRadius: 8, border: 'none', background: 'var(--primary, #f43f5e)', color: '#fff', fontWeight: 600 }}
              >
                Install
              </button>
            )}
            <button
              onClick={() => setPermissionStep(true)}
              style={{ padding: '0.6rem 1.1rem', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent' }}
            >
              Done — it's installed
            </button>
            <button
              onClick={dismiss}
              style={{ padding: '0.6rem 1.1rem', borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--text-muted)' }}
            >
              Not now
            </button>
          </div>
        </>
      ) : (
        <>
          <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.05rem', fontWeight: 700 }}>One more thing</h2>
          <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            Allow notifications so Valentine can alert you about urgent assignments and when work you assigned is completed.
          </p>
          <div style={{ display: 'flex', gap: '0.6rem' }}>
            <button
              onClick={handleAllow}
              style={{ padding: '0.6rem 1.1rem', borderRadius: 8, border: 'none', background: 'var(--primary, #f43f5e)', color: '#fff', fontWeight: 600 }}
            >
              Allow
            </button>
            <button onClick={dismiss} style={{ padding: '0.6rem 1.1rem', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent' }}>
              Skip
            </button>
          </div>
        </>
      )}
    </div>
  );
}
